"use client";

import { useId, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { Smartphone, Globe } from "@/components/icons";
import MobilePricingCards from "../../products/mobile-proxies/pricing-cards";
import ResidentialPricingCards from "../../products/residential-proxies/pricing-cards";

type ProxyType = "mobile" | "residential";

const OPTIONS: {
  id: ProxyType;
  label: string;
  icon: typeof Smartphone;
}[] = [
  { id: "mobile", label: "Mobile Proxies", icon: Smartphone },
  { id: "residential", label: "Residential Proxies", icon: Globe },
];

export default function ProxyPricingToggle({
  defaultType = "mobile",
}: {
  defaultType?: ProxyType;
}) {
  const [active, setActive] = useState<ProxyType>(defaultType);
  const reduced = useReducedMotion() ?? false;
  const tablistId = useId();

  return (
    <>
      <section
        id="proxy-pricing"
        aria-label="Choose proxy type"
        className="pt-section relative pb-6"
      >
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Pricing</p>
          <h2 className="text-foreground mb-3 text-3xl font-bold tracking-tight sm:text-4xl">
            Get started with Proxidize
          </h2>
          <p className="text-muted-foreground mx-auto mb-8 max-w-xl text-base leading-relaxed">
            Pick the network that fits your workflow. Switch any time.
          </p>

          <div
            role="tablist"
            aria-label="Proxy type"
            id={tablistId}
            className="border-border bg-card/60 relative inline-flex items-center gap-1 rounded-full border p-1.5 backdrop-blur-sm"
          >
            {OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isActive = opt.id === active;
              return (
                <button
                  key={opt.id}
                  role="tab"
                  type="button"
                  aria-selected={isActive}
                  aria-controls={`${tablistId}-${opt.id}-panel`}
                  id={`${tablistId}-${opt.id}-tab`}
                  onClick={() => setActive(opt.id)}
                  className="focus-visible:ring-ring focus-visible:ring-offset-background relative inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:px-5"
                  style={{
                    color: isActive
                      ? "var(--brand-green)"
                      : "color-mix(in srgb, var(--foreground) 70%, transparent)",
                  }}
                >
                  {isActive && (
                    <motion.span
                      layoutId={`${tablistId}-active`}
                      className="absolute inset-0 rounded-full border"
                      style={{
                        background: "color-mix(in srgb, var(--brand-green) 7%, transparent)",
                        borderColor: "color-mix(in srgb, var(--brand-green) 25%, transparent)",
                      }}
                      transition={
                        reduced ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 32 }
                      }
                      aria-hidden="true"
                    />
                  )}
                  <Icon size={14} aria-hidden="true" className="relative" />
                  <span className="relative">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={active}
          id={`${tablistId}-${active}-panel`}
          role="tabpanel"
          aria-labelledby={`${tablistId}-${active}-tab`}
          initial={reduced ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          {active === "mobile" ? (
            <MobilePricingCards hideHeader />
          ) : (
            <ResidentialPricingCards hideHeader />
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
}
